/**
 * AdminHealthDashboard.jsx
 * Admin dashboard for viewing all customers' fitness and glucose tracking data
 * Respects customer privacy settings - only shows what customers have shared
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Activity, Heart, Footprints, Flame, Droplet, Users,
  RefreshCw, TrendingUp, TrendingDown, Minus, Search,
  ChevronRight, Eye, EyeOff, Calendar, BarChart3,
  AlertTriangle, CheckCircle, Clock, Filter, Download
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatStat } from '../../utils/format';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle 
} from '@/components/ui/dialog';
import CustomerFitnessView from './CustomerFitnessView';
import GlucoseCorrelationChart from './GlucoseCorrelationChart';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminHealthDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('fitness');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Fitness data
  const [fitnessOverview, setFitnessOverview] = useState({ customers: [], summary: {} });
  
  // Glucose data
  const [glucoseOverview, setGlucoseOverview] = useState({ customers: [], total_tracking: 0 });
  
  // Selected customer for detail view
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerDetailOpen, setCustomerDetailOpen] = useState(false);
  const [customerGlucoseData, setCustomerGlucoseData] = useState(null);

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    
    try {
      const [fitnessRes, glucoseRes] = await Promise.allSettled([
        axios.get(`${API}/fitness-tracking/admin/overview`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/glucose/admin/overview`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (fitnessRes.status === 'fulfilled') {
        setFitnessOverview(fitnessRes.value.data);
      }
      if (glucoseRes.status === 'fulfilled') {
        setGlucoseOverview(glucoseRes.value.data);
      }
    } catch (error) {
      console.error('Error loading health data:', error);
      toast.error('Fehler beim Laden der Gesundheitsdaten');
    } finally {
      setLoading(false);
    }
  };

  const loadCustomerGlucose = async (customerId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${API}/glucose/admin/customer/${customerId}?hours=48`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCustomerGlucoseData(res.data);
    } catch (error) {
      console.error('Error loading customer glucose:', error);
      setCustomerGlucoseData({ enabled: false, message: 'Fehler beim Laden' });
    }
  };

  const openCustomerDetail = (customer, type) => {
    setSelectedCustomer({ ...customer, type });
    if (type === 'glucose') {
      loadCustomerGlucose(customer.customer_id);
    }
    setCustomerDetailOpen(true);
  };

  // Filter customers by search
  const filteredFitnessCustomers = fitnessOverview.customers.filter(c => 
    c.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  const filteredGlucoseCustomers = glucoseOverview.customers?.filter(c => 
    c.name?.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  // Get trend indicator
  const getTrendBadge = (value, type) => {
    if (type === 'steps') {
      if (value >= 10000) return <Badge className="bg-green-500/20 text-green-400">Sehr aktiv</Badge>;
      if (value >= 7000) return <Badge className="bg-blue-500/20 text-blue-400">Aktiv</Badge>;
      if (value >= 5000) return <Badge className="bg-yellow-500/20 text-yellow-400">Moderat</Badge>;
      return <Badge className="bg-red-500/20 text-red-400">Wenig aktiv</Badge>;
    }
    if (type === 'glucose') {
      if (value >= 70 && value <= 140) return <Badge className="bg-green-500/20 text-green-400">Im Ziel</Badge>;
      if (value < 70) return <Badge className="bg-red-500/20 text-red-400">Niedrig</Badge>;
      return <Badge className="bg-orange-500/20 text-orange-400">Erhöht</Badge>;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-yellow-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="admin-health-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-yellow-600" />
            Gesundheits-Dashboard
          </h1>
          <p className="text-gray-500 mt-1">
            Übersicht aller Kunden mit Fitness- und Blutzucker-Tracking
          </p>
        </div>
        <Button onClick={loadAllData} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Aktualisieren
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-blue-600">Fitness aktiv</p>
                <p className="text-2xl font-bold text-blue-900">
                  {formatStat(fitnessOverview.summary?.active_customers)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <Footprints className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-green-600">Ø Schritte/Tag</p>
                <p className="text-2xl font-bold text-green-900">
                  {Math.round((fitnessOverview.summary?.avg_steps_per_customer || 0) / 7).toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-500/20 rounded-lg">
                <Droplet className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-xs text-red-600">Blutzucker aktiv</p>
                <p className="text-2xl font-bold text-red-900">
                  {formatStat(glucoseOverview.total_tracking)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-500/20 rounded-lg">
                <Flame className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-xs text-orange-600">Kalorien (7 Tage)</p>
                <p className="text-2xl font-bold text-orange-900">
                  {(fitnessOverview.summary?.total_calories_7d || 0).toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          placeholder="Kunde suchen..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="fitness" className="flex items-center gap-2">
            <Activity className="w-4 h-4" />
            Fitness ({filteredFitnessCustomers.length})
          </TabsTrigger>
          <TabsTrigger value="glucose" className="flex items-center gap-2">
            <Droplet className="w-4 h-4" />
            Blutzucker ({filteredGlucoseCustomers.length})
          </TabsTrigger>
        </TabsList>

        {/* Fitness Tab */}
        <TabsContent value="fitness" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Fitness-Aktivität (Letzte 7 Tage)</CardTitle>
              <CardDescription>
                Kunden mit aktiver Fitness-Synchronisation
              </CardDescription>
            </CardHeader>
            <CardContent>
              {filteredFitnessCustomers.length > 0 ? (
                <div className="space-y-2">
                  {/* Table Header */}
                  <div className="grid grid-cols-6 gap-4 p-3 bg-gray-50 rounded-lg text-sm font-medium text-gray-600">
                    <div>Kunde</div>
                    <div className="text-right">Schritte</div>
                    <div className="text-right">Kalorien</div>
                    <div className="text-right">Aktive Tage</div>
                    <div>Status</div>
                    <div className="text-right">Aktion</div>
                  </div>
                  
                  {/* Table Rows */}
                  {filteredFitnessCustomers.map((customer, idx) => (
                    <div 
                      key={idx}
                      className="grid grid-cols-6 gap-4 p-3 bg-white border rounded-lg items-center hover:bg-gray-50 transition-colors"
                    >
                      <div>
                        <p className="font-medium text-gray-900">{customer.name || 'Unbekannt'}</p>
                        <p className="text-xs text-gray-500">
                          {customer.last_sync ? `Sync: ${new Date(customer.last_sync).toLocaleDateString('de-DE')}` : 'Kein Sync'}
                        </p>
                      </div>
                      <div className="text-right">
                        <span className="font-semibold text-blue-600">
                          {formatStat(customer.total_steps?.toLocaleString())}
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="font-semibold text-orange-600">
                          {formatStat(customer.total_calories?.toLocaleString())}
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="font-semibold text-green-600">
                          {formatStat(customer.days_active)} / 7
                        </span>
                      </div>
                      <div>
                        {getTrendBadge(Math.round(customer.total_steps / 7), 'steps')}
                      </div>
                      <div className="text-right">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => openCustomerDetail(customer, 'fitness')}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Details
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Activity className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Keine Kunden mit Fitness-Tracking gefunden</p>
                  <p className="text-sm">Kunden müssen ihre Fitness-Apps verbinden</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Glucose Tab */}
        <TabsContent value="glucose" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Blutzucker-Übersicht (Heute)</CardTitle>
              <CardDescription>
                Kunden mit aktivem CGM-Tracking
              </CardDescription>
            </CardHeader>
            <CardContent>
              {filteredGlucoseCustomers.length > 0 ? (
                <div className="space-y-2">
                  {/* Table Header */}
                  <div className="grid grid-cols-6 gap-4 p-3 bg-gray-50 rounded-lg text-sm font-medium text-gray-600">
                    <div>Kunde</div>
                    <div className="text-right">Aktuell</div>
                    <div className="text-right">Ø Heute</div>
                    <div className="text-right">Min / Max</div>
                    <div>Status</div>
                    <div className="text-right">Aktion</div>
                  </div>
                  
                  {/* Table Rows */}
                  {filteredGlucoseCustomers.map((customer, idx) => (
                    <div 
                      key={idx}
                      className="grid grid-cols-6 gap-4 p-3 bg-white border rounded-lg items-center hover:bg-gray-50 transition-colors"
                    >
                      <div>
                        <p className="font-medium text-gray-900">{customer.name || 'Unbekannt'}</p>
                        <p className="text-xs text-gray-500">
                          {customer.readings_today} Messungen heute
                        </p>
                      </div>
                      <div className="text-right">
                        <span className={`font-semibold text-lg ${
                          customer.current >= 70 && customer.current <= 140 
                            ? 'text-green-600' 
                            : customer.current < 70 
                              ? 'text-red-600' 
                              : 'text-orange-600'
                        }`}>
                          {customer.current || '—'}
                        </span>
                        <span className="text-xs text-gray-500 ml-1">mg/dL</span>
                      </div>
                      <div className="text-right">
                        <span className="font-semibold text-gray-700">
                          {customer.today_avg || '—'}
                        </span>
                        <span className="text-xs text-gray-500 ml-1">mg/dL</span>
                      </div>
                      <div className="text-right text-sm">
                        <span className="text-blue-600">{customer.today_min || '—'}</span>
                        <span className="text-gray-400 mx-1">/</span>
                        <span className="text-orange-600">{customer.today_max || '—'}</span>
                      </div>
                      <div>
                        {getTrendBadge(customer.current, 'glucose')}
                      </div>
                      <div className="text-right">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => openCustomerDetail(customer, 'glucose')}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Details
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Droplet className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Keine Kunden mit Blutzucker-Tracking gefunden</p>
                  <p className="text-sm">Kunden müssen CGM-Geräte verbinden und Tracking aktivieren</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Customer Detail Dialog */}
      <Dialog open={customerDetailOpen} onOpenChange={setCustomerDetailOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedCustomer?.type === 'fitness' ? (
                <Activity className="w-5 h-5 text-blue-600" />
              ) : (
                <Droplet className="w-5 h-5 text-red-600" />
              )}
              {selectedCustomer?.name} - {selectedCustomer?.type === 'fitness' ? 'Fitness' : 'Blutzucker'} Details
            </DialogTitle>
          </DialogHeader>
          
          <div className="mt-4">
            {selectedCustomer?.type === 'fitness' ? (
              <CustomerFitnessView 
                customerId={selectedCustomer.customer_id}
                customerName={selectedCustomer.name}
              />
            ) : (
              /* Glucose Detail View - Now with correlation chart */
              <GlucoseCorrelationChart
                customerId={selectedCustomer?.customer_id}
                customerName={selectedCustomer?.name}
                hours={48}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminHealthDashboard;
